/**
 * The board view's numbers, in CSS pixels on screen unless they say board units: how far
 * around the screen items are still drawn, a frame's title, arrowheads, pictures coming in,
 * the crop handles, where the style bar sits, the text sizes it offers, and how a picture of the
 * board is made. The board's own state (zoom, snapping, sizes of new items) is in
 * `state/board/constants.ts`.
 */

/** How far beyond the screen (pixels) items are still drawn, so a pan never shows them late. */
export const MARGIN_PX = 200;

/** The size of a frame's title on screen (CSS pixels), whatever the zoom; never smaller in board units. */
export const FRAME_TITLE_PX = 14;

/** How long an arrowhead is, in board units. */
export const ARROW_HEAD_PX = 12;

/** The longest side (board units) a picture comes in with; smaller ones keep their own size. */
export const PICTURE_SIDE = 480;

/** The shortest side (board units) a picture comes in with, however thin it is. */
export const PICTURE_MIN_SIDE = 16;

/** How far apart pictures put on the board together are (board units). */
export const PICTURE_GAP = 24;

/** The handles' sizes on screen (CSS pixels): a corner's arms, a side's bar, their thickness, and the room a side's bar is grabbed in. */
export const CROP_HANDLE_PX = { corner: 18, side: 22, thick: 4, hit: 18 } as const;

/** How far under the part kept its bar sits (CSS pixels). */
export const CROP_BAR_GAP_PX = 12;

/** The bar never goes higher than this on the board (CSS pixels from its top), so it stays under the board's own bars. */
export const BAR_TOP_MIN_PX = 48;

/** How far above the selection the bar sits (CSS pixels), and how much higher still over a frame's title, which stays clickable. */
export const BAR_ABOVE_PX = { item: 14, frameTitle: 26 } as const;

/** Room kept between the bar and the board's sides (CSS pixels). */
export const BAR_SIDE_PX = 8;

/** The sizes offered under the size field (CSS pixels). */
export const SIZE_CHOICES = [10, 12, 14, 18, 24, 36, 48, 64, 80, 144, 288];

/** Below this size the − and + buttons step by 1, from it by 2 (CSS pixels). */
export const FINE_STEPS_BELOW = 12;

/** Room around what is drawn (board units). */
export const EXPORT_MARGIN_PX = 32;

/** Room above a frame for its title (board units). */
export const EXPORT_TITLE_ROOM_PX = 28;

/** The longest side of a picture, in pixels (browsers refuse much bigger canvases). */
export const EXPORT_MAX_SIDE_PX = 8192;

/** The most pixels a picture holds. */
export const EXPORT_MAX_PIXELS = 48_000_000;

/** How many pixels per board unit when the picture is small enough (sharp on fine screens). */
export const EXPORT_PIXEL_RATIO = 2;
